import { useState, useMemo } from 'react'
import { Trophy, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { SkeletonTable } from '@/components/ui/Skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useEventData } from '@/contexts/EventDataContext'

type Dimension = 'visitors' | 'ulr' | 'credits' | 'sought-out'
type Category = 'attractions' | 'employees' | 'kiosks' | 'plans'

interface RankRow {
  rank: number
  name: string
  subtitle: string
  primary: number
  primaryLabel: string
  secondary: number
  secondaryLabel: string
  performance?: string
  zScore?: number
}

const DIMENSION_LABELS: Record<Dimension, { label: string; description: string }> = {
  visitors: { label: 'By Visitors', description: 'Unique visitors per attraction' },
  ulr: { label: 'By ULR Usage', description: 'Unlimited ride pass scans — repeat riders' },
  credits: { label: 'By Credit Sales', description: 'Per-ride credit purchases' },
  'sought-out': { label: 'Most Sought Out', description: 'Avg inbound transit distance — rides people travel to' },
}

const CATEGORY_LABELS: Record<Category, string> = {
  attractions: 'Attractions',
  employees: 'Employees',
  kiosks: 'Kiosks',
  plans: 'Plans',
}

export function RankingsPage() {
  const { currentEventId } = useAuth()
  const { entities, intelligence, loading, error } = useEventData()
  const [dimension, setDimension] = useState<Dimension>('visitors')
  const [category, setCategory] = useState<Category>('attractions')

  const attractionDimensionRows = useMemo<RankRow[]>(() => {
    if (!entities?.attractions) return []
    const attractions = entities.attractions

    switch (dimension) {
      case 'visitors':
        return [...attractions]
          .sort((a, b) => b.unique_guests - a.unique_guests)
          .map((a, i) => ({
            rank: i + 1,
            name: a.attraction_name,
            subtitle: `${a.attraction_type} · ${a.active_hours}h active`,
            primary: a.unique_guests,
            primaryLabel: 'visitors',
            secondary: a.revenue,
            secondaryLabel: 'revenue',
            performance: a.performance,
            zScore: a.z_score,
          }))
      case 'ulr':
        return [...attractions]
          .sort((a, b) => (b.total_scans - b.paid_scans) - (a.total_scans - a.paid_scans))
          .map((a, i) => ({
            rank: i + 1,
            name: a.attraction_name,
            subtitle: `${a.attraction_type} · ${a.repeat_play_pct.toFixed(0)}% repeat play`,
            primary: a.total_scans - a.paid_scans,
            primaryLabel: 'ULR scans',
            secondary: a.total_scans,
            secondaryLabel: 'total scans',
          }))
      case 'credits':
        return [...attractions]
          .sort((a, b) => b.paid_scans - a.paid_scans)
          .map((a, i) => ({
            rank: i + 1,
            name: a.attraction_name,
            subtitle: `${a.attraction_type} · $${a.revenue.toLocaleString()} rev`,
            primary: a.paid_scans,
            primaryLabel: 'credit scans',
            secondary: a.revenue,
            secondaryLabel: 'revenue',
          }))
      case 'sought-out': {
        const transitNodes = intelligence?.transit_graph?.nodes ?? []
        const transitEdges = intelligence?.transit_graph?.edges ?? []
        const inboundDist: Record<number, { totalDist: number; count: number }> = {}
        for (const edge of transitEdges) {
          if (!inboundDist[edge.to]) inboundDist[edge.to] = { totalDist: 0, count: 0 }
          inboundDist[edge.to].totalDist += edge.distance_m * edge.observations
          inboundDist[edge.to].count += edge.observations
        }
        const nodeRevenue: Record<number, number> = {}
        for (const node of transitNodes) {
          nodeRevenue[node.id] = node.revenue
        }
        return [...attractions]
          .map(a => {
            const stats = inboundDist[a.attraction_id]
            const avgDist = stats && stats.count > 0 ? stats.totalDist / stats.count : 0
            return { ...a, avgInboundDist: avgDist }
          })
          .sort((a, b) => b.avgInboundDist - a.avgInboundDist)
          .map((a, i) => ({
            rank: i + 1,
            name: a.attraction_name,
            subtitle: `${a.attraction_type} · ${a.unique_guests} visitors`,
            primary: Math.round(a.avgInboundDist),
            primaryLabel: 'avg inbound m',
            secondary: a.revenue,
            secondaryLabel: 'revenue',
          }))
      }
    }
  }, [entities, intelligence, dimension])

  const entityRows = useMemo<RankRow[]>(() => {
    if (!entities) return []
    switch (category) {
      case 'attractions':
        return attractionDimensionRows
      case 'employees':
        return entities.employees.humans.map((e, i) => ({
          rank: i + 1,
          name: e.name,
          subtitle: `${e.assigned_attraction} · ${e.role}`,
          primary: e.revenue,
          primaryLabel: 'revenue',
          secondary: e.scans_per_hour,
          secondaryLabel: 'scans/hr',
        }))
      case 'kiosks':
        return entities.kiosks.map(k => ({
          rank: k.rank,
          name: k.kiosk_name,
          subtitle: `${k.kiosk_group} · ${k.kiosk_type}`,
          primary: k.revenue,
          primaryLabel: 'revenue',
          secondary: k.orders_per_hour,
          secondaryLabel: 'orders/hr',
        }))
      case 'plans':
        return entities.plans.map(p => ({
          rank: p.rank,
          name: p.plan_name,
          subtitle: p.plan_type,
          primary: p.revenue,
          primaryLabel: 'revenue',
          secondary: p.units_sold,
          secondaryLabel: 'units sold',
        }))
    }
  }, [entities, category, attractionDimensionRows])

  const avgPrimary = entityRows.length > 0
    ? entityRows.reduce((s, r) => s + r.primary, 0) / entityRows.length
    : 0

  if (currentEventId == null) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <Trophy className="h-5 w-5 text-butler-600" />
          </div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Rankings</h1>
        </div>
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-ink-soft">
          Select an event to see performance rankings.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <Trophy className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Rankings</h1>
          <p className="text-sm text-ink-soft">Performance leaderboards across your operation</p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {(Object.keys(CATEGORY_LABELS) as Category[]).map(cat => (
          <button
            key={cat}
            type="button"
            onClick={() => setCategory(cat)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              cat === category
                ? 'border-butler-600 text-butler-700'
                : 'border-transparent text-ink-soft hover:text-ink-dark hover:border-slate-300'
            }`}
          >
            {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Dimension pills — only show for attractions */}
      {category === 'attractions' && (
        <div className="flex flex-wrap gap-2">
          {(Object.keys(DIMENSION_LABELS) as Dimension[]).map(dim => (
            <button
              key={dim}
              type="button"
              onClick={() => setDimension(dim)}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                dim === dimension
                  ? 'bg-butler-600 text-white shadow-brand-tinted'
                  : 'bg-slate-100 text-ink-soft hover:bg-butler-50 hover:text-butler-700'
              }`}
            >
              {DIMENSION_LABELS[dim].label}
            </button>
          ))}
        </div>
      )}
      {category === 'attractions' && (
        <p className="text-xs text-ink-soft -mt-3">{DIMENSION_LABELS[dimension].description}</p>
      )}

      {loading && !entities && <SkeletonTable rows={8} />}

      {error && !loading && entityRows.length === 0 && (
        <div className="rounded-lg border border-hairline bg-surface p-8 text-center">
          <Trophy className="h-10 w-10 text-ink-soft/30 mx-auto mb-3" />
          <p className="text-sm font-medium text-ink-dark">No ranking data yet</p>
          <p className="text-xs text-ink-soft mt-1">Select an event with scan data to see attraction, employee, and plan rankings.</p>
        </div>
      )}

      {!loading && entityRows.length > 0 && (
        <>
          <div className="mm-card-v3 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft w-12">#</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Name</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">
                    {entityRows[0]?.primaryLabel || 'Primary'}
                  </th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">
                    {entityRows[0]?.secondaryLabel || 'Secondary'}
                  </th>
                  {category === 'attractions' && dimension === 'visitors' && (
                    <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">vs Avg</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {entityRows.map(row => {
                  const vsAvg = avgPrimary > 0
                    ? Math.round(((row.primary - avgPrimary) / avgPrimary) * 100)
                    : 0
                  const isPrimMoney = row.primaryLabel === 'revenue'
                  const isSecMoney = row.secondaryLabel === 'revenue'
                  return (
                    <tr key={`${row.rank}-${row.name}`} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4">
                        <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                          row.rank === 1 ? 'bg-amber-100 text-amber-700' :
                          row.rank === 2 ? 'bg-slate-100 text-slate-600' :
                          row.rank === 3 ? 'bg-orange-50 text-orange-600' :
                          'bg-slate-50 text-ink-soft'
                        }`}>
                          {row.rank}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-ink-dark">{row.name}</div>
                        <div className="text-xs text-ink-soft">{row.subtitle}</div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="mm-data text-sm font-medium text-ink-dark">
                          {isPrimMoney ? '$' : ''}{row.primary.toLocaleString()}
                          {row.primaryLabel === 'avg inbound m' ? 'm' : ''}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="mm-data text-sm text-ink-soft">
                          {isSecMoney ? '$' : ''}{row.secondary.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                        </span>
                      </td>
                      {category === 'attractions' && dimension === 'visitors' && (
                        <td className="py-3 px-4 text-right">
                          <DeltaBadge value={vsAvg} />
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SummaryCard
              title="Top Performer"
              value={entityRows[0]?.name || '—'}
              subtitle={`${entityRows[0]?.primaryLabel}: ${entityRows[0]?.primary.toLocaleString()}`}
              variant="positive"
            />
            <SummaryCard
              title="Needs Attention"
              value={entityRows[entityRows.length - 1]?.name || '—'}
              subtitle={`${entityRows[0]?.primaryLabel}: ${entityRows[entityRows.length - 1]?.primary.toLocaleString()}`}
              variant="negative"
            />
            <SummaryCard
              title="Category Average"
              value={Math.round(avgPrimary).toLocaleString()}
              subtitle={`Across ${entityRows.length} items`}
              variant="neutral"
            />
          </div>
        </>
      )}

      {!loading && entityRows.length === 0 && entities && (
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-ink-soft">
          No {CATEGORY_LABELS[category].toLowerCase()} data available for this event.
        </div>
      )}
    </div>
  )
}

function DeltaBadge({ value }: { value: number }) {
  if (value > 5) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
        <TrendingUp className="h-3 w-3" />
        +{value}%
      </span>
    )
  }
  if (value < -5) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
        <TrendingDown className="h-3 w-3" />
        {value}%
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-soft">
      <Minus className="h-3 w-3" />
      avg
    </span>
  )
}

function SummaryCard({ title, value, subtitle, variant }: {
  title: string; value: string; subtitle: string; variant: 'positive' | 'negative' | 'neutral'
}) {
  const bgClass = variant === 'positive' ? 'border-green-200 bg-green-50/50' :
    variant === 'negative' ? 'border-red-200 bg-red-50/50' : 'border-slate-200 bg-slate-50/50'
  return (
    <div className={`rounded-lg border p-4 ${bgClass}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-ink-soft">{title}</div>
      <div className="mt-1 text-lg font-display font-bold text-ink-dark">{value}</div>
      <div className="mt-0.5 text-xs text-ink-soft">{subtitle}</div>
    </div>
  )
}
